import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FieldGroup =
  | 'lead-system'
  | 'lead-custom'
  | 'contact-system'
  | 'contact-custom'
  | 'company-system'
  | 'company-custom';

export interface FieldOption {
  /** Опциональный код системного поля (если нет id) */
  code?: string;
  /** Опциональный числовой id кастомного поля Kommo */
  id?: number;
  /** Что показывать в списке */
  label: string;
  /** Подзаголовок (тип/group) */
  hint?: string;
  /** Группа */
  group: FieldGroup;
  /** Сущность Kommo (lead / contact / company) */
  entity: 'lead' | 'contact' | 'company';
  /** Подсказка типа Kommo для автозаполнения */
  kommoType?: string;
}

const GROUP_TITLES: Record<FieldGroup, string> = {
  'lead-system': 'Сделка — системные',
  'lead-custom': 'Сделка — custom fields',
  'contact-system': 'Контакт — системные',
  'contact-custom': 'Контакт — custom fields',
  'company-system': 'Компания — системные',
  'company-custom': 'Компания — custom fields',
};

const GROUP_ORDER: FieldGroup[] = [
  'lead-system',
  'lead-custom',
  'contact-system',
  'contact-custom',
  'company-system',
  'company-custom',
];

interface Props {
  options: FieldOption[];
  /**
   * Текущее значение: формат "<entity>:id:123" или "<entity>:code:name".
   * Для обратной совместимости старые pipeline (без entity) приходят как "id:123" / "code:name" — это lead.
   */
  value: string;
  /** Текущая entity (нужна для разрешения коллизий по id между разными сущностями) */
  currentEntity?: 'lead' | 'contact' | 'company';
  onChange: (opt: FieldOption | null, encodedValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function KommoFieldCombobox({
  options,
  value,
  currentEntity = 'lead',
  onChange,
  placeholder = '— выбрать —',
  disabled,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => {
    if (!value) return null;
    // Парсинг "<entity>:id:123" или "<entity>:code:name", либо legacy "id:123"/"code:name" (entity = lead).
    let entity: 'lead' | 'contact' | 'company' = currentEntity;
    let rest = value;
    if (
      value.startsWith('lead:') ||
      value.startsWith('contact:') ||
      value.startsWith('company:')
    ) {
      const idx = value.indexOf(':');
      entity = value.slice(0, idx) as typeof entity;
      rest = value.slice(idx + 1);
    }
    if (rest.startsWith('id:')) {
      const id = Number(rest.slice(3));
      return options.find((o) => o.id === id && o.entity === entity) ?? null;
    }
    if (rest.startsWith('code:')) {
      const code = rest.slice(5);
      return options.find((o) => o.code === code && o.entity === entity) ?? null;
    }
    return null;
  }, [value, options, currentEntity]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = q
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            (o.hint ?? '').toLowerCase().includes(q) ||
            (o.code ?? '').toLowerCase().includes(q),
        )
      : options;
    const byGroup = {} as Record<FieldGroup, FieldOption[]>;
    for (const g of GROUP_ORDER) byGroup[g] = [];
    for (const o of all) byGroup[o.group].push(o);
    return byGroup;
  }, [options, query]);

  // Закрытие по клику вне
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs"
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-2 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по полю…"
              className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            {query && (
              <button onClick={() => setQuery('')}>
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto p-1">
            {GROUP_ORDER.map((g) => {
              const items = filtered[g];
              if (items.length === 0) return null;
              return (
                <div key={g}>
                  <div className="mt-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {GROUP_TITLES[g]} ({items.length})
                  </div>
                  {items.map((o) => (
                    <OptionRow
                      key={`${g}-${o.id ?? o.code}`}
                      option={o}
                      selected={selected}
                      onPick={(opt) => {
                        const inner =
                          opt.id !== undefined ? `id:${opt.id}` : `code:${opt.code}`;
                        onChange(opt, `${opt.entity}:${inner}`);
                        setOpen(false);
                        setQuery('');
                      }}
                    />
                  ))}
                </div>
              );
            })}

            {GROUP_ORDER.every((g) => filtered[g].length === 0) && (
              <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                Ничего не найдено
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionRow({
  option,
  selected,
  onPick,
}: {
  option: FieldOption;
  selected: FieldOption | null;
  onPick: (o: FieldOption) => void;
}): JSX.Element {
  const isSelected =
    selected !== null &&
    selected.entity === option.entity &&
    ((option.id !== undefined && selected.id === option.id) ||
      (option.code !== undefined && selected.code === option.code));
  return (
    <button
      type="button"
      onClick={() => onPick(option)}
      className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
    >
      <div className="flex flex-col">
        <span className={cn('truncate', isSelected && 'font-medium')}>{option.label}</span>
        {option.hint && <span className="truncate text-[10px] text-muted-foreground">{option.hint}</span>}
      </div>
      {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
    </button>
  );
}
